import React, { useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, Table, TableHead, TableBody, TableRow,
  TableCell, TableContainer, TableSortLabel, TablePagination, Checkbox, Chip, Button,
  Select, MenuItem, FormControl, InputLabel, TextField, InputAdornment, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Tooltip,
  LinearProgress } from '@mui/material';
import { Search, CheckCircle, Cancel, HourglassEmpty, Visibility } from '@mui/icons-material';
import { selectionAPI, decisionsAPI } from '../../services/api';
import { useSnackbar } from '../../utils/notify';
import dayjs from 'dayjs';

var DECISION_COLOR = { accepted: 'success', rejected: 'error', waitlisted: 'warning' };

export default function Selection() {
  var sn = useSnackbar(); var notify = sn.enqueueSnackbar;

  var fs = useState([]); var forms = fs[0]; var setForms = fs[1];
  var fi = useState(''); var formId = fi[0]; var setFormId = fi[1];
  var rs = useState(null); var rows = rs[0]; var setRows = rs[1];
  var ts = useState(0); var total = ts[0]; var setTotal = ts[1];
  var sm = useState({}); var summary = sm[0]; var setSummary = sm[1];
  var pg = useState(0); var page = pg[0]; var setPage = pg[1];
  var pp = useState(25); var perPage = pp[0]; var setPerPage = pp[1];
  var so = useState('avg_score'); var sort = so[0]; var setSort = so[1];
  var dr = useState('desc'); var dir = dr[0]; var setDir = dr[1];
  var df = useState('all'); var decisionFilter = df[0]; var setDecisionFilter = df[1];
  var qs = useState(''); var query = qs[0]; var setQuery = qs[1];
  var sel = useState([]); var selected = sel[0]; var setSelected = sel[1];
  var bk = useState(''); var bulk = bk[0]; var setBulk = bk[1];
  var nt = useState(''); var bulkNote = nt[0]; var setBulkNote = nt[1];
  var bs = useState(false); var busy = bs[0]; var setBusy = bs[1];
  var dt = useState(null); var detail = dt[0]; var setDetail = dt[1];

  useEffect(function() {
    selectionAPI.forms().then(function(r) {
      var list = r.data.data || [];
      setForms(list);
      if (list.length > 0) setFormId(list[0].id);
    });
  }, []);

  function load() {
    if (!formId) return;
    setRows(null);
    selectionAPI.applications({
      form_id: formId, page: page + 1, per_page: perPage,
      sort: sort, dir: dir,
      decision: decisionFilter === 'all' ? undefined : decisionFilter,
      q: query || undefined,
    }).then(function(r) {
      setRows(r.data.data.data || []);
      setTotal(r.data.data.total || 0);
      setSummary(r.data.data.summary || {});
      setSelected([]);
    }).catch(function() { setRows([]); });
  }

  useEffect(load, [formId, page, perPage, sort, dir, decisionFilter]);

  // Search is debounced so each keystroke does not hit the database.
  useEffect(function() {
    var t = setTimeout(function() { setPage(0); load(); }, 400);
    return function() { clearTimeout(t); };
  }, [query]);

  function toggleSort(col) {
    if (sort === col) { setDir(dir === 'desc' ? 'asc' : 'desc'); }
    else { setSort(col); setDir(col === 'applicant_name' ? 'asc' : 'desc'); }
    setPage(0);
  }

  function toggleRow(id) {
    setSelected(selected.indexOf(id) >= 0
      ? selected.filter(function(x) { return x !== id; })
      : selected.concat([id]));
  }

  function applyBulk() {
    if (!bulk || selected.length === 0) return;
    setBusy(true);
    decisionsAPI.make({ application_ids: selected, decision: bulk, notes: bulkNote })
      .then(function() {
        notify(selected.length + ' marked ' + bulk, {variant:'success'});
        setBulk(''); setBulkNote('');
        load();
      })
      .catch(function(e) {
        notify((e.response && e.response.data && e.response.data.error) || 'Failed', {variant:'error'});
      })
      .then(function() { setBusy(false); });
  }

  function decide(id, decision) {
    setBusy(true);
    decisionsAPI.make({ application_ids: [id], decision: decision, notes: '' })
      .then(function() { notify('Marked ' + decision, {variant:'success'}); load(); })
      .catch(function() { notify('Failed', {variant:'error'}); })
      .then(function() { setBusy(false); });
  }

  function open(id) {
    selectionAPI.application(id).then(function(r) { setDetail(r.data.data); })
      .catch(function() { notify('Could not open that application', {variant:'error'}); });
  }

  var E = React.createElement;

  if (forms.length === 0) {
    return E(Box, null,
      E(Typography, {variant:'h4', sx:{mb:2}}, 'Selection'),
      E(Card, null, E(CardContent, {sx:{textAlign:'center', py:8}},
        E(Typography, {variant:'h6', sx:{mb:1}}, 'No scholarship forms'),
        E(Typography, {color:'text.secondary'},
          'This workspace covers scholarship forms only.'))));
  }

  var undecided = summary.undecided || 0;
  var accepted = summary.accepted || 0;
  var rejected = summary.rejected || 0;
  var waitlisted = summary.waitlisted || 0;
  var fullyReviewed = summary.fully_reviewed || 0;

  function header(col, label, align) {
    return E(TableCell, {align: align || 'left'},
      E(TableSortLabel, {active: sort === col, direction: sort === col ? dir : 'desc',
        onClick: function() { toggleSort(col); }}, label));
  }

  // ── detail dialog ──────────────────────────────────────
  var detailBody = null;
  if (detail) {
    var schema = typeof detail.form_schema === 'string' ? JSON.parse(detail.form_schema) : detail.form_schema;
    if (typeof schema === 'string') { try { schema = JSON.parse(schema); } catch (e) { schema = {}; } }
    var responses = typeof detail.responses === 'string' ? JSON.parse(detail.responses) : (detail.responses || {});
    var criteria = typeof detail.criteria === 'string' ? JSON.parse(detail.criteria) : (detail.criteria || []);
    var reviewStages = typeof detail.review_stages === 'string'
      ? JSON.parse(detail.review_stages) : (detail.review_stages || []);
    var staged = !!detail.use_review_stages && reviewStages.length > 0;
    var fields = ((schema && schema.fields) || []).slice()
      .sort(function(a, b) { return (a.order || 0) - (b.order || 0); });
    var sections = ((schema && schema.sections) || []).slice()
      .sort(function(a, b) { return (a.order || 0) - (b.order || 0); });

    function criterionName(id) {
      var c = criteria.filter(function(x) { return x.id === id; })[0];
      return c ? c.name : id.substring(0, 8);
    }

    function answerBlock(f) {
      var v = responses[f.id];
      if (v === undefined || v === null || v === '' || (Array.isArray(v) && !v.length)) return null;
      return E(Box, {key:f.id, sx:{mb:1.5}},
        E(Typography, {variant:'caption', color:'text.secondary', fontWeight:600}, f.label),
        E(Typography, {variant:'body2', sx:{whiteSpace:'pre-wrap', wordBreak:'break-word'}},
          Array.isArray(v) ? v.join(', ') : String(v)));
    }

    detailBody = E(Box, {sx:{display:'flex', gap:3, flexDirection:{xs:'column', md:'row'}}},
      // Application
      E(Box, {sx:{flex:1, minWidth:0}},
        E(Typography, {variant:'subtitle1', fontWeight:700, sx:{mb:1.5}}, 'Application'),
        sections.length > 0
          ? sections.map(function(sec) {
              var sf = fields.filter(function(f) { return f.section_id === sec.id; })
                .map(answerBlock).filter(Boolean);
              if (sf.length === 0) return null;
              return E(Box, {key:sec.id, sx:{mb:2.5}},
                E(Typography, {variant:'subtitle2', color:'primary.main', fontWeight:700,
                  sx:{borderBottom:1, borderColor:'divider', pb:0.5, mb:1}}, sec.title),
                sf);
            })
          : fields.map(answerBlock).filter(Boolean)),

      // Reviews
      E(Box, {sx:{width:{xs:'100%', md:420}, flexShrink:0}},
        E(Typography, {variant:'subtitle1', fontWeight:700, sx:{mb:1.5}},
          'Reviews (' + detail.reviews.length + ')'),
        detail.reviews.length === 0
          ? E(Alert, {severity:'info'}, 'No reviewer has been assigned yet.')
          : detail.reviews.map(function(rv) {
              var sc = typeof rv.scores === 'string' ? JSON.parse(rv.scores) : (rv.scores || {});
              return E(Card, {key:rv.id, variant:'outlined', sx:{mb:1.5}},
                E(CardContent, {sx:{py:1.5}},
                  E(Box, {sx:{display:'flex', justifyContent:'space-between',
                    alignItems:'center', gap:1, mb:1}},
                    E(Box, {sx:{minWidth:0}},
                      E(Typography, {variant:'body2', fontWeight:600, noWrap:true}, rv.reviewer_name),
                      E(Typography, {variant:'caption',
                        color: rv.status === 'completed' ? 'text.secondary' : 'warning.main'},
                        rv.status === 'completed' && rv.submitted_at
                          ? dayjs(rv.submitted_at).format('MMM D, HH:mm')
                          : 'not submitted \u2014 excluded from the average')),
                    E(Chip, {size:'small',
                      label: typeof rv.final_score === 'number' ? rv.final_score.toFixed(2) : '\u2014',
                      color: rv.status === 'completed' ? 'primary' : 'default',
                      variant:'outlined'})),

                  (function() {
                    var recs = rv.stage_recommendations;
                    if (typeof recs === 'string') { try { recs = JSON.parse(recs); } catch (e5) { recs = {}; } }
                    recs = recs || {};
                    // With stages, each part carries its own recommendation.
                    if (staged && Object.keys(recs).length > 0) {
                      return E(Box, {sx:{display:'flex', gap:0.5, flexWrap:'wrap', mb:1}},
                        reviewStages.map(function(st) {
                          if (!recs[st.id]) return null;
                          return E(Chip, {key:st.id, size:'small', variant:'outlined',
                            label: st.label + ': ' + recs[st.id].replace('_', ' ')});
                        }));
                    }
                    return rv.recommendation
                      ? E(Chip, {size:'small', sx:{mb:1},
                          label: rv.recommendation.replace('_', ' ')}) : null;
                  })(),

                  Object.keys(sc).length > 0
                    ? E(Table, {size:'small', sx:{mb:1}},
                        E(TableBody, null,
                          Object.keys(sc).map(function(cid) {
                            var entry = sc[cid] || {};
                            var shown = (typeof entry.score === 'number') ? String(entry.score)
                              : (entry.answer || '\u2014');
                            return E(TableRow, {key:cid},
                              E(TableCell, {sx:{border:0, py:0.25, pl:0}},
                                E(Typography, {variant:'caption'}, criterionName(cid))),
                              E(TableCell, {sx:{border:0, py:0.25}, align:'right'},
                                E(Typography, {variant:'caption', fontWeight:600}, shown)),
                              E(TableCell, {sx:{border:0, py:0.25, pr:0, maxWidth:150}},
                                entry.comment
                                  ? E(Tooltip, {title:entry.comment},
                                      E(Typography, {variant:'caption', color:'text.secondary',
                                        noWrap:true}, entry.comment))
                                  : null));
                          })))
                    : null,

                  rv.overall_comment
                    ? E(Box, {sx:{bgcolor:'grey.50', p:1, borderRadius:1}},
                        E(Typography, {variant:'caption', sx:{whiteSpace:'pre-wrap'}},
                          rv.overall_comment))
                    : null));
            })));
  }

  return E(Box, null,
    E(Box, {sx:{display:'flex', justifyContent:'space-between', alignItems:'center',
      flexWrap:'wrap', gap:2, mb:2}},
      E(Typography, {variant:'h4'}, 'Selection'),
      E(FormControl, {size:'small', sx:{minWidth:280}},
        E(InputLabel, null, 'Scholarship form'),
        E(Select, {value:formId, label:'Scholarship form',
          onChange:function(e){ setFormId(e.target.value); setPage(0); }},
          forms.map(function(f) {
            return E(MenuItem, {key:f.id, value:f.id}, f.title + '  (' + f.applications + ')');
          })))),

    E(Card, {sx:{mb:2}},
      E(CardContent, {sx:{display:'flex', gap:1, flexWrap:'wrap', alignItems:'center'}},
        E(Chip, {label:total + ' applications'}),
        E(Chip, {label:fullyReviewed + ' fully reviewed', variant:'outlined'}),
        E(Chip, {label:accepted + ' accepted', color:'success',
          variant: accepted ? 'filled' : 'outlined'}),
        E(Chip, {label:waitlisted + ' waitlisted', color:'warning',
          variant: waitlisted ? 'filled' : 'outlined'}),
        E(Chip, {label:rejected + ' rejected', color:'error',
          variant: rejected ? 'filled' : 'outlined'}),
        E(Chip, {label:undecided + ' undecided', variant:'outlined'}))),

    E(Box, {sx:{display:'flex', gap:1.5, flexWrap:'wrap', alignItems:'center', mb:2}},
      E(TextField, {size:'small', placeholder:'Search name, email or ID', value:query,
        onChange:function(e){ setQuery(e.target.value); },
        sx:{flex:'1 1 260px', maxWidth:360},
        InputProps:{startAdornment:E(InputAdornment, {position:'start'},
          E(Search, {fontSize:'small'}))}}),
      E(FormControl, {size:'small', sx:{minWidth:180}},
        E(InputLabel, null, 'Decision'),
        E(Select, {value:decisionFilter, label:'Decision',
          onChange:function(e){ setDecisionFilter(e.target.value); setPage(0); }},
          E(MenuItem, {value:'all'}, 'All'),
          E(MenuItem, {value:'none'}, 'Undecided'),
          E(MenuItem, {value:'accepted'}, 'Accepted'),
          E(MenuItem, {value:'waitlisted'}, 'Waitlisted'),
          E(MenuItem, {value:'rejected'}, 'Rejected')))),

    selected.length > 0
      ? E(Card, {sx:{mb:2, bgcolor:'primary.50', border:1, borderColor:'primary.light'}},
          E(CardContent, {sx:{display:'flex', gap:1.5, flexWrap:'wrap', alignItems:'center'}},
            E(Typography, {variant:'body2', fontWeight:600}, selected.length + ' selected'),
            E(FormControl, {size:'small', sx:{minWidth:160}},
              E(InputLabel, null, 'Set decision'),
              E(Select, {value:bulk, label:'Set decision',
                onChange:function(e){ setBulk(e.target.value); }},
                E(MenuItem, {value:'accepted'}, 'Accepted'),
                E(MenuItem, {value:'waitlisted'}, 'Waitlisted'),
                E(MenuItem, {value:'rejected'}, 'Rejected'))),
            E(TextField, {size:'small', label:'Note (optional)', value:bulkNote,
              onChange:function(e){ setBulkNote(e.target.value); }, sx:{flex:'1 1 220px'}}),
            E(Button, {variant:'contained', disabled:!bulk || busy, onClick:applyBulk},
              'Apply to ' + selected.length),
            E(Button, {onClick:function(){ setSelected([]); }}, 'Clear')))
      : null,

    E(Card, null,
      rows === null ? E(LinearProgress) : null,
      E(TableContainer, null,
        E(Table, {size:'small'},
          E(TableHead, null,
            E(TableRow, null,
              E(TableCell, {padding:'checkbox'},
                E(Checkbox, {size:'small',
                  checked: rows !== null && rows.length > 0 && selected.length === rows.length,
                  indeterminate: selected.length > 0 && rows !== null && selected.length < rows.length,
                  onChange:function(e) {
                    setSelected(e.target.checked ? (rows || []).map(function(x){ return x.id; }) : []);
                  }})),
              header('applicant_name', 'Applicant'),
              header('avg_score', 'Avg score', 'right'),
              header('reviews_done', 'Reviews', 'right'),
              header('decision', 'Decision'),
              header('submitted_at', 'Submitted'),
              E(TableCell, {align:'right'}, 'Actions'))),
          E(TableBody, null,
            (rows || []).map(function(a) {
              return E(TableRow, {key:a.id, hover:true,
                selected: selected.indexOf(a.id) >= 0},
                E(TableCell, {padding:'checkbox'},
                  E(Checkbox, {size:'small', checked:selected.indexOf(a.id) >= 0,
                    onChange:function(){ toggleRow(a.id); }})),
                E(TableCell, null,
                  E(Typography, {variant:'body2'},
                    a.anonymous ? 'Application #' + a.id.substring(0, 8).toUpperCase()
                                : a.applicant_name),
                  !a.anonymous
                    ? E(Typography, {variant:'caption', color:'text.secondary'}, a.applicant_email)
                    : null),
                E(TableCell, {align:'right'},
                  typeof a.avg_score === 'number'
                    ? E(Chip, {size:'small', color:'primary', variant:'outlined',
                        label:a.avg_score.toFixed(2)})
                    : typeof a.avg_including_drafts === 'number'
                      ? E(Tooltip, {title:'From ' + a.drafts + ' unsubmitted review(s). Not counted until submitted.'},
                          E(Chip, {size:'small', variant:'outlined',
                            label:a.avg_including_drafts.toFixed(2) + ' draft'}))
                      : E(Typography, {variant:'caption', color:'text.secondary'}, '\u2014')),
                E(TableCell, {align:'right'},
                  E(Typography, {variant:'caption',
                    color: (a.assigned > 0 && a.reviews_done >= a.assigned)
                      ? 'success.main' : 'text.secondary'},
                    a.reviews_done + ' / ' + a.assigned),
                  a.drafts > 0
                    ? E(Typography, {variant:'caption', color:'warning.main',
                        sx:{display:'block'}}, a.drafts + ' in progress')
                    : null),
                E(TableCell, null,
                  a.decision
                    ? E(Chip, {size:'small', label:a.decision,
                        color: DECISION_COLOR[a.decision] || 'default'})
                    : E(Typography, {variant:'caption', color:'text.secondary'}, 'undecided')),
                E(TableCell, null,
                  E(Typography, {variant:'caption'},
                    dayjs(a.submitted_at).format('MMM D, YYYY'))),
                E(TableCell, {align:'right', sx:{whiteSpace:'nowrap'}},
                  E(Tooltip, {title:'Accept'},
                    E(Button, {size:'small', color:'success', disabled:busy,
                      onClick:function(){ decide(a.id, 'accepted'); }},
                      E(CheckCircle, {fontSize:'small'}))),
                  E(Tooltip, {title:'Waitlist'},
                    E(Button, {size:'small', color:'warning', disabled:busy,
                      onClick:function(){ decide(a.id, 'waitlisted'); }},
                      E(HourglassEmpty, {fontSize:'small'}))),
                  E(Tooltip, {title:'Reject'},
                    E(Button, {size:'small', color:'error', disabled:busy,
                      onClick:function(){ decide(a.id, 'rejected'); }},
                      E(Cancel, {fontSize:'small'}))),
                  E(Tooltip, {title:'Open'},
                    E(Button, {size:'small', onClick:function(){ open(a.id); }},
                      E(Visibility, {fontSize:'small'})))));
            }),
            (rows !== null && rows.length === 0)
              ? E(TableRow, null, E(TableCell, {colSpan:7, align:'center',
                  sx:{py:4, color:'text.secondary'}}, 'Nothing matches those filters'))
              : null))),
      E(TablePagination, {component:'div', count:total, page:page, rowsPerPage:perPage,
        rowsPerPageOptions:[25, 50, 100],
        onPageChange:function(_, p){ setPage(p); },
        onRowsPerPageChange:function(e){ setPerPage(parseInt(e.target.value, 10)); setPage(0); }})),

    E(Typography, {variant:'caption', color:'text.secondary', sx:{display:'block', mt:1}},
      'Sorting and filtering run across every application on this form, not just the page shown.'),

    detail ? E(Dialog, {open:true, onClose:function(){ setDetail(null); },
      maxWidth:'lg', fullWidth:true},
      E(DialogTitle, null,
        detail.applicant_name,
        E(Typography, {variant:'body2', color:'text.secondary'},
          detail.applicant_email + ' \u2022 ' + detail.form_title)),
      E(DialogContent, {dividers:true}, detailBody),
      E(DialogActions, {sx:{gap:1}},
        detail.decision
          ? E(Chip, {size:'small', label:'Currently ' + detail.decision,
              color: DECISION_COLOR[detail.decision] || 'default'})
          : null,
        E(Box, {sx:{flex:1}}),
        E(Button, {color:'success', disabled:busy,
          onClick:function(){ decide(detail.id, 'accepted'); setDetail(null); }}, 'Accept'),
        E(Button, {color:'warning', disabled:busy,
          onClick:function(){ decide(detail.id, 'waitlisted'); setDetail(null); }}, 'Waitlist'),
        E(Button, {color:'error', disabled:busy,
          onClick:function(){ decide(detail.id, 'rejected'); setDetail(null); }}, 'Reject'),
        E(Button, {onClick:function(){ setDetail(null); }}, 'Close'))) : null);
}
